import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface Factor {
  title: string;
  description: string;
}

interface Stat {
  value: string;
  label: string;
}

export function WhyDubai() {
  const { t, tArray } = useTranslation();
  const factors = tArray<Factor>('whyDubai.factors');
  const stats = tArray<Stat>('whyDubai.stats');

  return (
    <section
      id="market"
      className="bg-slate-50 dark:bg-[#0d1318] py-20 lg:py-28 relative"
    >
      {/* Decorative map element */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 overflow-hidden">
        <svg viewBox="0 0 300 600" className="w-full h-full">
          {/* Simplified MENA region outline */}
          <path
            d="M50 100 Q100 80 150 100 T250 120 Q280 150 260 200 T240 280 Q200 320 180 380 T120 450 Q80 480 60 440 T40 360 Q20 300 30 240 T50 100"
            stroke="#5a8a9a"
            strokeWidth="1"
            fill="none"
          />
          {/* City markers */}
          <circle cx="220" cy="180" r="4" fill="#7ab8c9" />
          <circle cx="200" cy="200" r="3" fill="#7ab8c9" opacity="0.6" />
          <circle cx="180" cy="220" r="3" fill="#7ab8c9" opacity="0.6" />
          <circle cx="160" cy="260" r="3" fill="#7ab8c9" opacity="0.4" />
          <circle cx="140" cy="300" r="3" fill="#7ab8c9" opacity="0.4" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('whyDubai.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl tracking-tight">
            {t('whyDubai.title')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            {t('whyDubai.description')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {factors.map((factor, index) => (
            <motion.div
              key={factor.title}
              className="group relative p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0a0f14]/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-slate-300 dark:hover:border-[#3d5a6e]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="flex items-start gap-4">
                <span className="text-slate-400 dark:text-[#3d5a6e] text-sm md:text-base mt-1 font-medium group-hover:text-[#3d6a7a] dark:group-hover:text-[#7ab8c9] transition-colors">
                  0{index + 1}
                </span>
                <div>
                  <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-xl md:text-2xl mb-3">
                    {factor.title}
                  </h3>
                  <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed">
                    {factor.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Regional context */}
        <motion.div
          className="mt-16 p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0a0f14]/50 backdrop-blur-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.value} className="text-center">
                <span className="font-display text-[#3d6a7a] dark:text-[#7ab8c9] text-3xl md:text-4xl block">
                  {stat.value}
                </span>
                <span className="text-slate-500 dark:text-[#6a7a7e] text-xs md:text-sm mt-2 block">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
